import { ThemeColorName } from '@examdojo/styles';
import type { ActionDefinition } from './widget-action.model';

// ==============================================================================
// UI CONTROL SYSTEM (The bindings)
// Maps external UI elements to internal Actions.
// ==============================================================================

/** Control types supported by the widget system */
export enum WidgetControlType {
  Button = 'button',
  ButtonGroup = 'button-group',
  Slider = 'slider',
  SliderGroup = 'slider-group',
  MathInput = 'math-input',
}

/** Where controls can be rendered */
export type WidgetControlPosition = 'top-bar' | 'bottom-bar' | 'canvas-overlay' | 'slider-footer';

/** Base control properties shared by all control types */
export interface WidgetControlBase<TType extends WidgetControlType = WidgetControlType> {
  /** Control type discriminator */
  type: TType;
  /** Unique ID for DOM keys */
  id: string;
  /** Human-readable label */
  label: string;
  /** Where to render this control */
  position?: WidgetControlPosition;
}

/**
 * Button control - triggers an action on click
 * Variants: text-only (no icon), icon-only (icon + showLabel:false), text+icon (icon + showLabel:true)
 */
export interface WidgetButtonControl extends WidgetControlBase<WidgetControlType.Button> {
  /** FontAwesome icon name (e.g. 'magnifying-glass-plus') */
  icon?: string;
  /** Whether to show the label text. Default: true if no icon, false if icon present */
  showLabel?: boolean;
  /** The Action to fire when clicked */
  trigger: ActionDefinition;
}

/** Button group control - renders as a segment (grouped related buttons) */
export interface WidgetButtonGroupControl extends WidgetControlBase<WidgetControlType.ButtonGroup> {
  /** Buttons in this group */
  buttons: Array<Omit<WidgetButtonControl, 'type' | 'position'>>;
}

/**
 * Slider control - numeric range input with optional editable input field.
 *
 * The `param` field uses dot-notation paths to target parameters:
 * - Simple: "amplitude" (for widgets without elements)
 * - Element.param: "f.a" (targets element 'f', param 'a' - graphpad style)
 *
 * The UPDATE_PARAM action is auto-generated from the param path.
 */
export interface WidgetSliderControl extends WidgetControlBase<WidgetControlType.Slider> {
  /**
   * Parameter path using dot notation.
   * - graphpad: "f.a" → element 'f', param 'a'
   * - simple widget: "amplitude" → param 'amplitude'
   */
  param: string;
  /** [min, max, step?] - Range and optional step for the slider */
  range: [number, number, number?];
  /** Default value (defaults to min if not provided) */
  defaultValue?: number;
  /** Whether to show an editable input box for direct value entry (default: false) */
  editable?: boolean;
  /** Color for the slider handle and trace */
  color?: ThemeColorName;
}

/** Slider group control - multiple sliders stacked in a styled container */
export interface WidgetSliderGroupControl extends WidgetControlBase<WidgetControlType.SliderGroup> {
  /** Sliders in this group */
  sliders: Array<Omit<WidgetSliderControl, 'type' | 'position'>>;
}

/** Helper to extract min from slider range */
export function getSliderMin(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[0] ?? 0;
}

/** Helper to extract max from slider range */
export function getSliderMax(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[1] ?? 100;
}

/** Helper to extract step from slider range (defaults to 1) */
export function getSliderStep(control: Pick<WidgetSliderControl, 'range'>): number {
  return control.range?.[2] ?? 1;
}

/**
 * Math input control - input field only (no slider) for numeric values.
 *
 * Like sliders, uses dot-notation param paths for UPDATE_PARAM action.
 */
export interface WidgetMathInputControl extends WidgetControlBase<WidgetControlType.MathInput> {
  /**
   * Parameter path using dot notation.
   * - graphpad: "f.a" → element 'f', param 'a'
   * - simple widget: "amplitude" → param 'amplitude'
   */
  param: string;
  /** Default value */
  defaultValue?: number;
}

/** Union of all control types */
export type WidgetControl =
  | WidgetButtonControl
  | WidgetButtonGroupControl
  | WidgetSliderControl
  | WidgetSliderGroupControl
  | WidgetMathInputControl;
